import { useRef } from 'react';
import { Decrement, Increment } from '@sas/react-svg';
import cx from 'classnames';
import { createUseTextLocalizer } from '../../../locale/createUseTextLocalizer';
import { postfixTestId } from '../../../util/testId';
import { ButtonVariant } from '../../Button';
import { BaseButton } from '../../Button/__internal__';
import { doAccuratePlus, isEqual, isLarger, isLess } from '../util';
import bundle from '../NovaComponents-NumericStepper-gui-icu.properties';
import cn from '../NumericStepper.css.json';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const useTextLocalizer = createUseTextLocalizer(bundle);
export const SpinButtons = ({
  children,
  className,
  disabled,
  minimum,
  maximum,
  nextValueOnInvalid = minimum,
  stepSize = 1,
  pageSize = doAccuratePlus(stepSize, stepSize),
  style,
  testId,
  tooltip,
  value,
  onValueChange,
  onValueCommit
}) => {
  const localize = useTextLocalizer();
  const preValueRef = useRef(null);
  const mouseClicking = useRef(false);
  const rootElementRef = useRef(null);
  const getNextValue = (increasing, stepSize) => {
    return calculateNextValue(value, increasing, stepSize, minimum, maximum, nextValueOnInvalid);
  };
  const fireValueChangeEvent = value => {
    if (value !== undefined) {
      onValueChange?.({
        newValue: value
      });
    }
  };
  const fireValueCommitEvent = (value, reason) => {
    onValueCommit?.({
      value,
      reason
    });
  };

  // This is requested in NOVACOMMONS-4457
  const onBlur = e => {
    if (!mouseClicking.current && !rootElementRef.current?.contains(e.relatedTarget)) {
      fireValueCommitEvent(undefined, 'blur');
    }
  };
  const upButtonDisabled = disabled || value !== undefined && !isLess(value, maximum);
  const downButtonDisabled = disabled || value !== undefined && !isLarger(value, minimum);
  const handleMousePress = increasing => {
    if (disabled) return;
    const nextValue = getNextValue(increasing, stepSize);
    preValueRef.current = value;
    mouseClicking.current = true;
    fireValueChangeEvent(nextValue);
  };
  const commitValue = reason => {
    if (preValueRef.current !== null && value !== undefined && (preValueRef.current === undefined || !isEqual(preValueRef.current, value))) {
      preValueRef.current = null;
      fireValueCommitEvent(value, reason);
    }
  };
  const handleMouseRelease = e => {
    if (e.button === 0) {
      e.preventDefault();
      mouseClicking.current = false;
      commitValue('click');
    }
  };
  const handleButtonKeyUp = e => {
    if (disabled) return;
    switch (e.key) {
      case 'Enter':
      case ' ':
        e.preventDefault();
        commitValue('keyin');
        break;
      default:
        break;
    }
  };
  const handleContainerKeyUp = e => {
    if (disabled) return;
    switch (e.key) {
      case 'ArrowDown':
      case 'ArrowUp':
      case 'PageDown':
      case 'PageUp':
      case 'Home':
      case 'End':
        e.preventDefault();
        commitValue('keyin');
        break;
      default:
        break;
    }
  };
  const handleContainerKeyDown = e => {
    if (disabled) return;
    let nextValue;
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        if (downButtonDisabled) return;
        nextValue = getNextValue(false, stepSize);
        break;
      case 'ArrowUp':
        e.preventDefault();
        if (upButtonDisabled) return;
        nextValue = getNextValue(true, stepSize);
        break;
      case 'PageDown':
        e.preventDefault();
        if (downButtonDisabled) return;
        nextValue = getNextValue(false, pageSize);
        break;
      case 'PageUp':
        e.preventDefault();
        if (upButtonDisabled) return;
        nextValue = getNextValue(true, pageSize);
        break;
      case 'Home':
        e.preventDefault();
        if (value && isEqual(value, minimum)) return;
        nextValue = minimum;
        break;
      case 'End':
        e.preventDefault();
        if (value && isEqual(value, maximum)) return;
        nextValue = maximum;
        break;
      default:
        break;
    }
    if (nextValue !== undefined) {
      preValueRef.current = value;
      fireValueChangeEvent(nextValue);
    }
  };
  return /*#__PURE__*/_jsxs("div", {
    ref: rootElementRef,
    className: cx(cn.container, className),
    style: style,
    title: tooltip,
    "data-testid": testId,
    onKeyDown: handleContainerKeyDown,
    onKeyUp: handleContainerKeyUp,
    children: [children, /*#__PURE__*/_jsx(BaseButton, {
      icon: {
        icon: Decrement,
        tooltip: ''
      },
      className: cx(cn.button, cn.downButton),
      onPress: () => {
        handleMousePress(false);
      },
      onKeyUp: handleButtonKeyUp,
      onMouseUp: handleMouseRelease,
      onMouseLeave: handleMouseRelease,
      focusable: false,
      onBlur: onBlur,
      disabled: downButtonDisabled,
      variant: ButtonVariant.Icon,
      ariaLabel: localize('decrementButton.aria'),
      testId: postfixTestId(testId, 'decrementButton'),
      enableContinuousPress: true
    }), /*#__PURE__*/_jsx(BaseButton, {
      icon: {
        icon: Increment,
        tooltip: ''
      },
      className: cx(cn.button, cn.upButton),
      onPress: () => {
        handleMousePress(true);
      },
      onKeyUp: handleButtonKeyUp,
      onMouseUp: handleMouseRelease,
      onMouseLeave: handleMouseRelease,
      onBlur: onBlur,
      focusable: false,
      disabled: upButtonDisabled,
      variant: ButtonVariant.Icon,
      ariaLabel: localize('incrementButton.aria'),
      testId: postfixTestId(testId, 'incrementButton'),
      enableContinuousPress: true
    })]
  });
};
function calculateNextValue(value, increasing, stepSize, min, max, nextValueOnInvalid) {
  let newValue;
  if (increasing) {
    if (value === undefined) {
      newValue = nextValueOnInvalid;
    } else if (isLess(value, min)) {
      newValue = nextValueOnInvalid;
    } else {
      newValue = doAccuratePlus(value, stepSize);
    }
    if (isLarger(newValue, max)) {
      newValue = max;
    }
  } else {
    if (value === undefined) {
      newValue = nextValueOnInvalid;
    } else if (isLarger(value, max)) {
      newValue = nextValueOnInvalid;
    } else {
      newValue = doAccuratePlus(value, -stepSize);
    }
    if (isLess(newValue, min)) {
      newValue = min;
    }
  }
  return newValue;
}
//# sourceMappingURL=SpinButtons.js.map